import { useState, useEffect, useReducer } from "react";
import { Trans, t } from "@lingui/macro";
import { X, Search, UserX, Loader2 } from "lucide-react";
import { User } from "../../models/User";
import { UserBlock } from "../../models/UserBlock";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";

import { Dialog, DialogContent } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "../../components/ui/avatar";
import { useToast } from "../../components/ui/use-toast";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import UserFind from "./UserFind";
import { Util } from "../../services/Utils/Util";

interface AccountBlockingModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const AccountBlockingModal: React.FC<AccountBlockingModalProps> = ({ open, onOpenChange }) => {
    const [_, forceUpdate] = useReducer((x) => x + 1, 0);
    const { toast } = useToast();

    const [blockConfirmUser, setBlockConfirmUser] = useState<User | null>(null);
    const [unblockConfirmBlock, setUnblockConfirmBlock] = useState<UserBlock | null>(null);
    const [inProgress, setInProgress] = useState(false);

    function getSortedList() {
        let list = Array.from(My.UserBlocks.values());
        return list.sort((a, b) => a.created < b.created ? 1 : -1);
    }

    function confirmUserBlock(user: User) {
        if (user.id === My.user.id) {
            toast({ title: t`You cannot block yourself.`, duration: 2000 });
            return;
        }
        if (My.UserBlocks.get(user.id)) {
            toast({ title: t`You have already blocked ${user.name}.`, duration: 2000 });
            return;
        }
        setBlockConfirmUser(user);
    }

    async function addUserBlock() {
        if (!blockConfirmUser) return;
        setInProgress(true);
        try {
            const block = new UserBlock(blockConfirmUser, My.user.id);
            My.UserBlocks.set(block.blockedUser.id, block);
            await Fire.saveUserBlock(block);
            toast({ title: t`Blocked ${blockConfirmUser.name}`, duration: 2000 });
            forceUpdate();
        } catch (e) {
            toast({ title: t`Failed to block user`, duration: 2000 });
        } finally {
            setInProgress(false);
            setBlockConfirmUser(null);
        }
    }

    async function removeUserBlock() {
        if (!unblockConfirmBlock) return;
        setInProgress(true);
        try {
            My.UserBlocks.delete(unblockConfirmBlock.blockedUser.id);
            await Fire.deleteUserBlock(My.user.id, unblockConfirmBlock.id);
            toast({ title: t`Unblocked ${unblockConfirmBlock.blockedUser.name}`, duration: 2000 });
            forceUpdate();
        } catch (e) {
            toast({ title: t`Failed to unblock user`, duration: 2000 });
        } finally {
            setInProgress(false);
            setUnblockConfirmBlock(null);
        }
    }

    const blockedList = getSortedList();

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-lg w-[95vw] max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b">
                        <h2 className="text-lg font-semibold"><Trans>Account Blocking</Trans></h2>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        {/* User Search */}
                        <div className="space-y-2">
                            <h3 className="text-sm font-medium text-muted-foreground"><Trans>Block users</Trans></h3>
                            <UserFind
                                onSelectedUser={confirmUserBlock}
                                placeholder={t`Find users to block`}
                                header={t`Block users`}
                                room={undefined}
                            />
                        </div>

                        {/* Blocked List */}
                        <div className="space-y-3">
                            <h3 className="text-sm font-medium text-muted-foreground">
                                {blockedList.length === 0
                                    ? <Trans>No blocked users</Trans>
                                    : <Trans>My blocked list ({blockedList.length})</Trans>
                                }
                            </h3>

                            {blockedList.length > 0 && (
                                <div className="space-y-2">
                                    {blockedList.map((block) => (
                                        <div
                                            key={block.blockedUser.id}
                                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                        >
                                            <div className="flex items-center gap-3">
                                                <Avatar className="w-10 h-10">
                                                    <AvatarImage src={block.blockedUser.photoUrl || Util.ANONYMOUS_PHOTO_URL} />
                                                    <AvatarFallback>{block.blockedUser.name?.charAt(0) || "?"}</AvatarFallback>
                                                </Avatar>
                                                <div>
                                                    <p className="font-medium text-sm">{block.blockedUser.name}</p>
                                                    <p className="text-xs text-muted-foreground">{block.blockedUser.email}</p>
                                                </div>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                onClick={() => setUnblockConfirmBlock(block)}
                                            >
                                                <Trans>Unblock</Trans>
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {blockedList.length === 0 && (
                                <div className="flex flex-col items-center justify-center py-8 text-center">
                                    <UserX className="w-12 h-12 text-gray-300 mb-3" />
                                    <p className="text-sm text-muted-foreground">
                                        <Trans>You haven't blocked anyone yet.</Trans>
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Block Confirmation */}
            <AlertDialog open={!!blockConfirmUser} onOpenChange={(open) => !open && setBlockConfirmUser(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle><Trans>Block {blockConfirmUser?.name}?</Trans></AlertDialogTitle>
                        <AlertDialogDescription>
                            <Trans>Messages created by this person will be hidden for you. We do not inform the person that you have blocked them.</Trans>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700"
                            onClick={addUserBlock}
                            disabled={inProgress}
                        >
                            {inProgress ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            <Trans>Block</Trans>
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Unblock Confirmation */}
            <AlertDialog open={!!unblockConfirmBlock} onOpenChange={(open) => !open && setUnblockConfirmBlock(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle><Trans>Unblock {unblockConfirmBlock?.blockedUser.name}?</Trans></AlertDialogTitle>
                        <AlertDialogDescription>
                            <Trans>Messages from {unblockConfirmBlock?.blockedUser.name} will now be visible. We don't inform them that you have unblocked.</Trans>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                        <AlertDialogAction onClick={removeUserBlock} disabled={inProgress}>
                            {inProgress ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            <Trans>Unblock</Trans>
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default AccountBlockingModal;
